/**
 * Company Overview — full grid of every active employee × every day in the
 * current month. Filterable by name, unit, role, day+activity.
 */
import { useState, useMemo, useEffect, useRef } from "preact/hooks";
import type { JSX } from "preact";
import { state, currentYear, currentMonth } from "../state/store";
import { dateKey, daysInMonth, isWeekend, formatDayLabel } from "../utils/dates";
import { workdaysOfMonth } from "../utils/workdays";
import { displayName, peopleForMonth } from "../utils/names";
import { STATUS_LABEL, type StatusCode } from "../types";
import { debounce } from "../utils/debounce";
import { MONTHS } from "../config";

/** Local storage key for the "compact rows" preference so it persists
 *  across sessions for admins reviewing the same big roster. */
const LS_COMPACT_KEY = "icons_company_compact_v1";

interface NoteTip {
  who: string;
  day: string;
  status: StatusCode | "";
  text: string;
  x: number;
  y: number;
  // Present only for the pinned meetings row: each distinct meeting that day
  // with the names of who's attending it (grouped, so duplicates merge but
  // the people are still visible).
  meetings?: Array<{ title: string; names: string[] }>;
}

const STATUS_OPTIONS: Array<[StatusCode | "", string]> = [
  ["", "All activities"],
  ["P", "In presence"],
  ["T", "Working trip"],
  ["R", "Remote working"],
  ["M", "In Milan office"],
  ["V", "In Rovereto Office"],
  ["A", "Available but not at desk"],
  ["O", "Out of office"],
];

export function CompanyOverview(): JSX.Element {
  const s = state.value;
  const y = currentYear.value;
  const m = currentMonth.value;
  const nDays = daysInMonth(y, m);

  const [search, setSearch] = useState("");
  // Multi-select: an array of selected unit names. Empty = no unit filter
  // (i.e. show all units).
  const [unitFilter, setUnitFilter] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusCode | "">("");
  const [dayFilter, setDayFilter] = useState<string>("");
  // "Incomplete only": show people who have at least one unfilled WORKING
  // day this month (weekends / holidays / closures excluded).
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [tip, setTip] = useState<NoteTip | null>(null);
  const [compact, setCompact] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_COMPACT_KEY) === "1";
    } catch {
      return false;
    }
  });
  // ── Mobile day-view (C1) ──────────────────────────────────────────
  // Which day the mobile "who's in" list shows. Deliberately SEPARATE from
  // dayFilter (the desktop "Any day" select) so the mobile view can never
  // change desktop filtering. Defaulted by an effect below.
  const [dayViewDay, setDayViewDay] = useState<number>(1);
  // Read-only detail sheet (person + day) opened by tapping a day-view row.
  const [detail, setDetail] = useState<{ who: string; day: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Tracks whether the chrome-collapse (cal-scrolled) is currently engaged.
  // Shared between the scroll listener and the reset-on-change effect so the
  // two never disagree. A ref (not state) because it must not cause renders.
  const wasScrolledRef = useRef(false);

  /** Auto-collapse the page chrome (Tabs + MonthBar + banners) when the
   *  user scrolls inside the calendar table — they came here to see rows,
   *  not navigation. Adds a body-level class so CSS can shrink the bars;
   *  cleaned up on unmount so other tabs don't inherit it.
   *
   *  Engaging `cal-scrolled` grows this scroll container (~80px) and hides
   *  chrome, which clamps the container's scrollTop downward. With a single
   *  threshold that clamp could push scrollTop back across the line and
   *  re-toggle on the next frame — an infinite flip-flop that reads as the
   *  table "shaking", most visible with a short (filtered) result set whose
   *  height sits right at the boundary. Two guards remove it:
   *    1. Hysteresis — engage at 120px, release at 24px. The 96px gap is
   *       wider than the ~80px height delta, so one clamp can never flip the
   *       state back (and a result set that only overflows by <80px can
   *       never reach 120px, so it simply never collapses).
   *    2. An rAF throttle so we evaluate at most once per frame. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const evaluate = (): void => {
      raf = 0;
      const top = el.scrollTop;
      const next = wasScrolledRef.current ? top > 24 : top > 120;
      if (next !== wasScrolledRef.current) {
        wasScrolledRef.current = next;
        document.body.classList.toggle("cal-scrolled", next);
      }
    };
    const onScroll = (): void => {
      if (raf) return;
      raf = requestAnimationFrame(evaluate);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
      document.body.classList.remove("cal-scrolled");
    };
  }, []);

  /** When the visible dataset changes — month, year, or any filter — reset
   *  the table scroll to the top and release the collapsed-chrome state.
   *  Changing month while scrolled down used to leave the view mid-table
   *  with chrome collapsed and a short result set parked exactly on the
   *  oscillation boundary; starting clean at the top sidesteps that. */
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
    wasScrolledRef.current = false;
    document.body.classList.remove("cal-scrolled");
  }, [y, m, search, unitFilter, roleFilter, statusFilter, dayFilter, incompleteOnly]);

  // Default the mobile day-view to today (or the first weekday when browsing
  // another month). Runs on mount + whenever the visible month changes.
  // Reads only local primitives (y / m / nDays) — never a signal in deps.
  useEffect(() => {
    const now = new Date();
    let d = 1;
    if (now.getFullYear() === y && now.getMonth() === m) {
      d = now.getDate();
    } else {
      while (d < nDays && isWeekend(y, m, d)) d++;
    }
    setDayViewDay(Math.min(Math.max(1, d), nDays));
  }, [y, m, nDays]);

  // Esc closes the read-only detail sheet.
  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setDetail(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail]);

  const toggleCompact = (): void => {
    setCompact((v) => {
      const next = !v;
      try {
        localStorage.setItem(LS_COMPACT_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Show the styled tooltip on hover. The tooltip uses position:fixed
  // so we pass viewport-relative coordinates straight from
  // getBoundingClientRect (no scroll offset needed).
  const showTip = (
    e: MouseEvent,
    who: string,
    day: string,
    code: StatusCode | "",
    text: string,
    meetings?: Array<{ title: string; names: string[] }>,
  ): void => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTip({
      who,
      day,
      status: code,
      text,
      x: r.left + r.width / 2,
      y: r.bottom + 8,
      meetings,
    });
  };
  const hideTip = (): void => setTip(null);

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 140), []);

  const units = useMemo(() => {
    const u = new Set<string>();
    Object.values(s.roster).forEach((r) => r.unit && u.add(r.unit));
    return [...u].sort();
  }, [s.roster]);
  const roles = useMemo(() => {
    const r = new Set<string>();
    Object.values(s.roster).forEach((row) => row.role && r.add(row.role));
    return [...r].sort();
  }, [s.roster]);

  const dayKey = dayFilter ? dateKey(y, m, parseInt(dayFilter, 10)) : null;

  // Show active people PLUS former employees whose last recorded day
  // is in/after the visible month. So a colleague who left after May 7
  // still shows in May's overview but vanishes from June onward.
  const visibleYearMonth = `${y}-${String(m + 1).padStart(2, "0")}`;
  const people = peopleForMonth(s.roster, visibleYearMonth);
  // Working-day keys for the visible month — same definition the Dashboard
  // uses (excludes weekends, Italian public holidays and company closures).
  // A person is "incomplete" if any of these days has no calendar entry.
  const monthWorkdayKeys = useMemo(
    () => workdaysOfMonth(y, m, s.closures).map((d) => dateKey(y, m, d)),
    [y, m, s.closures],
  );
  const filtered = people.filter((name) => {
    const info = s.roster[name];
    if (!info) return false;
    const lcSearch = search.toLowerCase().trim();
    if (lcSearch) {
      const hay = `${name} ${displayName(name)} ${info.unit} ${info.role}`.toLowerCase();
      if (!hay.includes(lcSearch)) return false;
    }
    if (unitFilter.length > 0 && !unitFilter.includes(info.unit)) return false;
    if (roleFilter && info.role !== roleFilter) return false;
    if (statusFilter) {
      const cal = s.calendar[name] ?? {};
      if (dayKey) {
        if (cal[dayKey] !== statusFilter) return false;
      } else {
        let hit = false;
        for (let d = 1; d <= nDays; d++) {
          if (cal[dateKey(y, m, d)] === statusFilter) {
            hit = true;
            break;
          }
        }
        if (!hit) return false;
      }
    }
    if (incompleteOnly) {
      const cal = s.calendar[name] ?? {};
      const complete = monthWorkdayKeys.every((k) => cal[k] !== undefined);
      if (complete) return false;
    }
    return true;
  });

  const dayWeekdays = ["S", "M", "T", "W", "T", "F", "S"];

  // ── Mobile day-view derived data — a thin projection of `filtered`
  //    (the same search/unit/role/status set the desktop grid uses), kept
  //    deliberately read-only to match the desktop overview. ──
  const dvDay = Math.min(Math.max(1, dayViewDay), nDays);
  const dvKey = dateKey(y, m, dvDay);
  const dvWd = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][new Date(y, m, dvDay).getDay()];
  const dvIsWeekend = isWeekend(y, m, dvDay);
  const dayPeople = filtered.filter((name) => !!s.calendar[name]?.[dvKey]);
  const inOfficeCount = dayPeople.reduce((acc, name) => {
    const c = s.calendar[name]?.[dvKey];
    return acc + (c === "P" || c === "M" || c === "V" ? 1 : 0);
  }, 0);
  const anyFilterActive = !!(search || unitFilter.length || roleFilter || statusFilter);
  const stepDay = (delta: number): void =>
    setDayViewDay((d) => Math.min(nDays, Math.max(1, Math.min(Math.max(1, d), nDays) + delta)));

  return (
    <div class="calendar-card">
      <div class="cal-header">
        <h2>{`${["January","February","March","April","May","June","July","August","September","October","November","December"][m]} ${y}`}</h2>
        <div class="filter-bar">
          <input
            type="text"
            class="cal-search"
            placeholder="Search name…"
            onInput={(e) => debouncedSetSearch((e.target as HTMLInputElement).value)}
          />
          <UnitMultiSelect units={units} selected={unitFilter} onChange={setUnitFilter} />
          <select value={roleFilter} onChange={(e) => setRoleFilter((e.target as HTMLSelectElement).value)}>
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select class="day-select" value={dayFilter} onChange={(e) => setDayFilter((e.target as HTMLSelectElement).value)}>
            <option value="">Any day</option>
            {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => {
              const we = isWeekend(y, m, d);
              const wd = new Date(y, m, d).getDay();
              return (
                <option key={d} value={d} disabled={we}>
                  {dayWeekdays[wd]} {d}{we ? " (weekend)" : ""}
                </option>
              );
            })}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value as StatusCode | "")}
          >
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button
            type="button"
            class={`incomplete-toggle${incompleteOnly ? " active" : ""}`}
            onClick={() => setIncompleteOnly((v) => !v)}
            title="Show only people with unfilled working days this month (weekends, holidays and closures excluded)"
            aria-pressed={incompleteOnly}
          >
            ⚠ Incomplete
          </button>
          <button
            type="button"
            class="clear-filters"
            onClick={() => {
              setSearch(""); setUnitFilter([]); setRoleFilter(""); setStatusFilter(""); setDayFilter(""); setIncompleteOnly(false);
            }}
          >
            Clear
          </button>
          <button
            type="button"
            class={`compact-toggle${compact ? " active" : ""}`}
            onClick={toggleCompact}
            title="Tighter rows so more teammates fit on screen at once"
            aria-pressed={compact}
          >
            {compact ? "⤢ Comfortable" : "⤡ Compact"}
          </button>
        </div>
      </div>

      <div class={`cal-scroll${compact ? " cal-scroll-compact" : ""}`} ref={scrollRef}>
        <table class="cal">
          <thead>
            <tr>
              <th>Person</th>
              {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => (
                <th key={d} class={isWeekend(y, m, d) ? "weekend" : ""}>
                  <span class="d">{d}</span>
                  <span class="wd">{dayWeekdays[new Date(y, m, d).getDay()]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Pinned meetings row — for each day, list everyone who flagged
                an in-presence meeting AND whose calendar status that day is
                P (in presence). The list comes from state.meetings and is
                surfaced via the same hover tooltip pattern as notes.

                Closure days are NOT given their own pinned row anymore —
                redundant with the diagonal-stripe overlay each closure cell
                already gets in every employee's row. The heads-up banner
                between the header and the main view (ClosureBanner) still
                surfaces upcoming closures. */}
            <tr class="meetings-row">
              <td class="name-cell">
                <span class="nm">📅 In-presence meetings</span>
                <span class="rl">Flagged by the team</span>
              </td>
              {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => {
                const k = dateKey(y, m, d);
                const we = isWeekend(y, m, d);
                // Group the day's in-presence meetings BY TITLE, collecting the
                // attendee NAMES under each. Duplicates merge (one entry per
                // title) but you can still see who's on each meeting — only
                // people physically in count (P Lodi / M Milan / V Rovereto).
                const byTitle = new Map<string, string[]>();
                Object.keys(s.meetings).forEach((p) => {
                  const desc = s.meetings[p]?.[k];
                  if (desc === undefined) return;
                  const code = s.calendar[p]?.[k];
                  if (code !== "P" && code !== "M" && code !== "V") return;
                  const title = desc.trim() || "Untitled meeting";
                  const arr = byTitle.get(title) ?? [];
                  arr.push(displayName(p));
                  byTitle.set(title, arr);
                });
                const dayMeetings = [...byTitle.entries()]
                  .map(([title, names]) => ({ title, names: names.sort() }))
                  .sort((a, b) => a.title.localeCompare(b.title));
                const cnt = dayMeetings.length;
                const cls = ["day"];
                if (we) cls.push("weekend");
                if (cnt > 0) cls.push("has-meeting");
                // Native-title fallback: "Title — name1, name2" per line.
                const summary = dayMeetings
                  .map((mt) => `${mt.title} — ${mt.names.join(", ")}`)
                  .join("\n");
                return (
                  <td
                    key={d}
                    class={cls.join(" ")}
                    title={summary || ""}
                    onMouseEnter={
                      cnt > 0
                        ? (e) => showTip(e, "Meetings", k, "", summary, dayMeetings)
                        : undefined
                    }
                    onMouseLeave={cnt > 0 ? hideTip : undefined}
                  >
                    {cnt > 0 ? cnt : ""}
                  </td>
                );
              })}
            </tr>
            {filtered.length === 0 && (
              <tr>
                <td colspan={nDays + 1} class="empty-state">
                  No team members match the current filters
                </td>
              </tr>
            )}
            {filtered.map((name) => {
              const info = s.roster[name]!;
              const cal = s.calendar[name] ?? {};
              const noteMap = s.notes[name] ?? {};
              return (
                <tr key={name}>
                  <td class="name-cell">
                    <span class="nm">{displayName(name)}</span>
                    <span class="rl">
                      {info.unit || "—"}
                      <span class="brand-dot" aria-hidden="true" />
                      {info.role || "—"}
                    </span>
                  </td>
                  {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => {
                    const k = dateKey(y, m, d);
                    const code = cal[k];
                    const note = noteMap[k];
                    const we = isWeekend(y, m, d);
                    const closure = s.closures[k];
                    const isClosure = closure !== undefined;
                    const cls = ["day"];
                    if (we) cls.push("weekend");
                    if (code) cls.push(`s-${code}`);
                    if (note) cls.push("has-note");
                    if (isClosure) cls.push("closure");
                    // Native title is the immediate fallback (always works,
                    // even on touch). The styled tip is the prettier hover.
                    const nativeTitle = [
                      code ? STATUS_LABEL[code] : "",
                      note,
                    ].filter(Boolean).join("\n\n");
                    return (
                      <td
                        key={d}
                        class={cls.join(" ")}
                        title={nativeTitle}
                        onMouseEnter={
                          note
                            ? (e) => showTip(e, name, k, code ?? "", note)
                            : undefined
                        }
                        onMouseLeave={note ? hideTip : undefined}
                      >
                        {code ?? ""}
                        {note && <span class="note-pin" aria-hidden="true" />}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile day-view (C1) — replaces the dense grid at ≤720px via CSS.
          Answers "who's in on day X?" as a tappable list, with notes and
          meetings reachable on touch (the grid's tooltips are mouse-only).
          Read-only, matching the desktop overview. ── */}
      <section class="company-dayview" aria-label="Who's in, by day">
        <div class="dv-stepper">
          <button type="button" class="dv-arrow" onClick={() => stepDay(-1)} disabled={dvDay <= 1} aria-label="Previous day">‹</button>
          <div class="dv-daylabel">
            <span class="dv-wd">{dvWd}</span>
            <span class="dv-date">{dvDay} {MONTHS[m]} {y}</span>
          </div>
          <button type="button" class="dv-arrow" onClick={() => stepDay(1)} disabled={dvDay >= nDays} aria-label="Next day">›</button>
        </div>
        <div class="dv-headline">
          {dvIsWeekend ? <span class="dv-weekend">Weekend</span> : <><b>{inOfficeCount}</b> in the office</>}
        </div>
        {dayPeople.length === 0 ? (
          <div class="dv-empty">
            {anyFilterActive ? "No one matches the current filters on this day." : "No statuses set for this day yet."}
          </div>
        ) : (
          <ul class="dv-list">
            {dayPeople.map((name) => {
              const info = s.roster[name]!;
              const code = s.calendar[name]?.[dvKey];
              const note = s.notes[name]?.[dvKey];
              const mtg = s.meetings[name]?.[dvKey];
              return (
                <li key={name}>
                  <button type="button" class="dv-row" onClick={() => setDetail({ who: name, day: dvKey })}>
                    <span class="dv-row-main">
                      <span class="dv-name">{displayName(name)}</span>
                      <span class="dv-role">
                        {info.unit || "—"}
                        <span class="brand-dot" aria-hidden="true" />
                        {info.role || "—"}
                      </span>
                      {note && <span class="dv-note">{note}</span>}
                      {mtg !== undefined && <span class="dv-mtg">📅 {mtg.trim() || "Meeting"}</span>}
                    </span>
                    {code && <span class={`status-chip s-${code}`}>{STATUS_LABEL[code]}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {detail && (
        <div class="modal-backdrop open" role="presentation" onClick={() => setDetail(null)}>
          <div class="modal" role="dialog" aria-modal="true" aria-labelledby="dvDetailTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
            <h3 id="dvDetailTitle">{displayName(detail.who)}</h3>
            <div class="modal-date">{formatDayLabel(detail.day)}</div>
            <div class="dv-detail">
              <div class="dv-detail-row">
                <span class="dv-detail-label">Status</span>
                {(() => {
                  const c = s.calendar[detail.who]?.[detail.day];
                  return c
                    ? <span class={`status-chip s-${c}`}>{STATUS_LABEL[c]}</span>
                    : <span class="dv-detail-none">No status set</span>;
                })()}
              </div>
              {s.notes[detail.who]?.[detail.day] && (
                <div class="dv-detail-row">
                  <span class="dv-detail-label">Note</span>
                  <span class="dv-detail-text">{s.notes[detail.who]?.[detail.day]}</span>
                </div>
              )}
              {s.meetings[detail.who]?.[detail.day] !== undefined && (
                <div class="dv-detail-row">
                  <span class="dv-detail-label">In-presence meeting</span>
                  <span class="dv-detail-text">{s.meetings[detail.who]?.[detail.day]?.trim() || "Flagged (no title)"}</span>
                </div>
              )}
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-primary" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {tip && (
        <div
          class="note-tip open"
          role="tooltip"
          style={`left:${tip.x}px;top:${tip.y}px`}
        >
          {tip.meetings ? (
            <>
              <div class="nt-head">In-presence meetings · {formatDayLabel(tip.day)}</div>
              <ul class="nt-meetings">
                {tip.meetings.map((mt) => (
                  <li key={mt.title}>
                    <span class="nt-mtg-title">{mt.title}</span>
                    <span class="nt-mtg-names">{mt.names.join(", ")}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <div class="nt-head">
                {displayName(tip.who)} · {formatDayLabel(tip.day)}
                {tip.status && ` · ${STATUS_LABEL[tip.status]}`}
              </div>
              <div class="nt-body">{tip.text}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select unit filter — a compact checkbox dropdown so the Company
 * Overview can be filtered by several units at once (e.g. SC + SI + BI).
 * An empty selection means "all units". The popover closes on an
 * outside-click or the Escape key so it never lingers over the table.
 */
function UnitMultiSelect({
  units,
  selected,
  onChange,
}: {
  units: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (u: string): void => {
    onChange(selected.includes(u) ? selected.filter((x) => x !== u) : [...selected, u]);
  };

  const label =
    selected.length === 0
      ? "All units"
      : selected.length === 1
        ? selected[0]!
        : `${selected.length} units`;

  return (
    <div class="ms-wrap" ref={wrapRef}>
      <button
        type="button"
        class={`ms-trigger${selected.length > 0 ? " active" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span class="ms-label">{label}</span>
        <span class="ms-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div class="ms-menu" role="group" aria-label="Filter by unit">
          {units.map((u) => (
            <label key={u}>
              <input
                type="checkbox"
                checked={selected.includes(u)}
                onChange={() => toggle(u)}
              />
              {u}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
